import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ChatMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  intents?: string[];
}

export interface ChatSession {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

const now = () =>
  new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

const WELCOME_MSG: ChatMessage = {
  role: "assistant",
  content:
    "Welcome to NetOps Security Assistant. I can help you with CLI commands, troubleshooting playbooks, CVE lookups, and cloud networking across multiple vendors. What do you need?",
  timestamp: now(),
};

export function useChatSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MSG]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Load sessions list
  const loadSessions = useCallback(async () => {
    if (!user) {
      setSessions([]);
      setActiveSessionId(null);
      setMessages([WELCOME_MSG]);
      return;
    }
    setLoadingSessions(true);
    const { data } = await supabase
      .from("chat_sessions")
      .select("*")
      .order("updated_at", { ascending: false });
    setSessions((data as ChatSession[]) || []);
    setLoadingSessions(false);
  }, [user]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Load messages for a session
  const loadSession = useCallback(async (sessionId: string) => {
    setActiveSessionId(sessionId);
    const { data } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("session_id", sessionId)
      .order("created_at", { ascending: true });
    if (data?.length) {
      setMessages(
        data.map((m: any) => ({
          id: m.id,
          role: m.role as "user" | "assistant",
          content: m.content,
          intents: m.intents?.length ? m.intents : undefined,
          timestamp: new Date(m.created_at).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        }))
      );
    } else {
      setMessages([WELCOME_MSG]);
    }
  }, []);

  // Create a new session with org_id
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    if (activeSessionId) return activeSessionId;

    // Get user's org_id for the session
    const { data: membership } = await supabase
      .from("org_members")
      .select("org_id")
      .eq("user_id", user.id)
      .maybeSingle();

    const orgId = membership?.org_id || null;

    const insertData: any = { user_id: user.id, title: "New Chat" };
    if (orgId) insertData.org_id = orgId;

    const { data, error } = await supabase
      .from("chat_sessions")
      .insert(insertData)
      .select("id")
      .single();
    if (error || !data) return null;
    const id = data.id;
    setActiveSessionId(id);
    setSessions((prev) => [
      { id, title: "New Chat", created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
      ...prev,
    ]);
    return id;
  }, [user, activeSessionId]);

  // Persist a message
  const persistMessage = useCallback(
    async (sessionId: string, msg: ChatMessage) => {
      await supabase.from("chat_messages").insert({
        session_id: sessionId,
        role: msg.role,
        content: msg.content,
        intents: msg.intents || [],
      });
    },
    []
  );

  // Update session title from first user message
  const updateSessionTitle = useCallback(
    async (sessionId: string, firstMsg: string) => {
      const title = firstMsg.length > 60 ? firstMsg.slice(0, 57) + "..." : firstMsg;
      await supabase
        .from("chat_sessions")
        .update({ title, updated_at: new Date().toISOString() })
        .eq("id", sessionId);
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, title } : s))
      );
    },
    []
  );

  const startNewChat = useCallback(() => {
    setActiveSessionId(null);
    setMessages([WELCOME_MSG]);
  }, []);

  const deleteSession = useCallback(
    async (sessionId: string) => {
      await supabase.from("chat_sessions").delete().eq("id", sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) startNewChat();
    },
    [activeSessionId, startNewChat]
  );

  return {
    sessions,
    activeSessionId,
    messages,
    setMessages,
    loadingSessions,
    loadSession,
    ensureSession,
    persistMessage,
    updateSessionTitle,
    startNewChat,
    deleteSession,
  };
}
